import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useDebounce } from "../hooks/useDebounce";
import { SearchAPI, SearchResult } from "../api/searchApi";

const GlobalSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const debouncedQuery = useDebounce(query, 1000);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGlobalResults = async () => {
      if (!debouncedQuery.trim()) {
        setResults([]);
        setSearchCompleted(false);
        return;
      }

      setLoading(true);
      setSearchCompleted(false);

      try {
        const data = await SearchAPI.getProductsGlobal(debouncedQuery);
        const resultsArray = Array.isArray(data) ? data : (data?.data || []);
        setResults(resultsArray);
      } catch (error) {
        console.error("Ошибка глобального поиска", error);
        setResults([]);
      } finally {
        setLoading(false);
        setSearchCompleted(true);
      }
    };

    fetchGlobalResults();
  }, [debouncedQuery]);

  const handleSearch = (value: string) => {
    setQuery(value);
    setSearchParams({ q: value });
  };

  const handleSelectProduct = (item: SearchResult) => {
    navigate(`/product?part_id=${item.uniqueId}`);
  };


  return (
    <div className="max-w-screen-xl mx-auto px-4 py-10 w-full">
      {/* Search Input */}
      <div className="mb-8">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Введите артикул детали для глобального поиска..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
            autoFocus
          />
        </div>
      </div>
  
      {/* Results */}
      <div className="space-y-4">
        {loading && (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Поиск...</p>
          </div>
        )}
  
        {!loading && searchCompleted && results.length === 0 && query.trim() && (
          <div className="py-12 text-center">
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Ничего не найдено
            </h3>
            <p className="text-gray-500">
              Попробуйте изменить поисковый запрос или проверить корректность написания
            </p>
          </div>
        )}
  
        {!loading && searchCompleted && results.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Найдено {results.length} результатов
            </p>
          </div>
        )}
  
        {!loading &&
          results.map((item, index) => (
            <motion.div
              key={item.uniqueId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gray-100 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleSelectProduct(item)}
            >
              <div className="flex-1 w-full">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded text-gray-700">
                    {item.code}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {item.brandName}
                  </span>
                  <span className="text-base font-light text-gray-400">
                    {item.name}
                  </span>
                </div>
                
              </div>
            </motion.div>
          ))}
  
        {!query.trim() && (
          <div className="py-12 text-center">
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Введите поисковый запрос
            </h3>
            <p className="text-gray-500">
              Начните вводить артикул детали для поиска
            </p>
          </div>
        )}
      </div>
    </div>
  );
  
};

export default GlobalSearchPage;
